// src/mobile/event-manager.ts
import { MobileEventActionData, type NavigationEventType } from './types';
import { safeInvoke } from '../../../utils/function';
import {
  registerSpecificNotificationHandler,
  getLogger,
  type Logger,
} from '../../../bridge';
import { NativeAPIResponseMessage } from '@zoom/hybrid-jsbridge';
import { isAvailable } from 'jssdk/utils';

export type CallbackRegistry = Map<string, (eventData: any) => void>;

export const WILDCARD_ACTION_ID = '*';

export class EventManager {
  private static instance: EventManager;
  private callbacks: Map<NavigationEventType, CallbackRegistry> = new Map();
  private logger: Logger;

  private constructor() {
    this.logger = getLogger();
  }

  static getInstance(): EventManager {
    if (!EventManager.instance) {
      EventManager.instance = new EventManager();
    }
    return EventManager.instance;
  }

  registerCallback<T = unknown>(
    eventType: NavigationEventType,
    actionID: string,
    callback: (eventData: T) => void
  ): void {
    if (!this.callbacks.has(eventType)) {
      this.callbacks.set(eventType, new Map());
    }
    this.logger.info(`registerCallback for ${eventType}:${actionID}`);
    this.callbacks.get(eventType)?.set(actionID, callback);
  }

  unregisterCallback(eventType: NavigationEventType, actionID: string): void {
    this.callbacks.get(eventType)?.delete(actionID);
  }

  replaceCallback<T = unknown>(
    eventType: NavigationEventType,
    actionID: string | undefined,
    callback: ((eventData: T) => void) | undefined
  ): void {
    const action = actionID || WILDCARD_ACTION_ID;
    this.unregisterCallback(eventType, action);
    if (callback) {
      this.registerCallback(eventType, action, callback);
    }
  }

  removeEventListeners(eventType: NavigationEventType): void {
    this.callbacks.delete(eventType);
  }

  triggerCallback(
    eventType: NavigationEventType,
    actionID: string,
    payload: any
  ): void {
    const action = actionID === undefined ? WILDCARD_ACTION_ID : actionID;
    const callback = this.callbacks.get(eventType)?.get(action);
    if (callback) {
      const finalPayload = actionID
        ? { ...payload, actionID: action }
        : { ...payload };
      safeInvoke(callback, finalPayload);
    } else {
      this.logger.warn(`no found callback for ${eventType}:${action}`);
    }

    if (action !== WILDCARD_ACTION_ID) {
      const wildcardCallback = this.callbacks
        .get(eventType)
        ?.get(WILDCARD_ACTION_ID);
      if (wildcardCallback) {
        safeInvoke(wildcardCallback, { ...payload, actionID: action });
      }
    }
  }
}

let eventInited = false;
export function setupEventListenersIfNeeded() {
  if (!eventInited) {
    // Initialize native event listeners
    registerSpecificNotificationHandler(
      ['UnifyNotify_OnMobileUIAction'],
      (notification: NativeAPIResponseMessage<MobileEventActionData>) => {
        const {
          result: { eventType, actionID, data },
        } = notification;
        EventManager.getInstance().triggerCallback(
          eventType as NavigationEventType,
          actionID,
          data
        );
      }
    );
    eventInited = true;
  }
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.registerEvent
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.5.0
 * android    6.5.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function registerEvent(
  eventType: NavigationEventType,
  actionId: string,
  callback: (data?: any) => void
) {
  await isAvailable('mobile.registerEvent');
  setupEventListenersIfNeeded();
  EventManager.getInstance().registerCallback(eventType, actionId, callback);
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.unregisterEvent
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.5.0
 * android    6.5.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function unregisterEvent(
  eventType: NavigationEventType,
  actionId: string
) {
  await isAvailable('mobile.unregisterEvent');
  setupEventListenersIfNeeded();
  EventManager.getInstance().unregisterCallback(eventType, actionId);
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.removeEventAllListeners
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.5.0
 * android    6.5.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function removeEventAllListeners(eventType: NavigationEventType) {
  await isAvailable('mobile.registerEvent');
  setupEventListenersIfNeeded();
  EventManager.getInstance().removeEventListeners(eventType);
}
